// BuildingStabilityInfoComponent.js
import { useEffect, useState } from "react";
import {
  SectionHeader,
  EditableSelectComponent,
  EditableTextareaComponent,
} from "components";
import { Document } from "assets/icons"; // Replace with appropriate icons
import { useSession } from "@clerk/clerk-react";

// Assuming BuildingStabilityInfoDTO is defined elsewhere in your application
import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { FileLabel } from "shared/types/file";
import {
  BuildingOwnerShipInfo,
  PreEmptionRightPresent,
} from "shared/types/ownership-info";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import FileLogTable from "shared/components/uploadFileModal/fileLogTable";

interface BuildingOwnershipInfoComponentProps {
  ownershipInfo: BuildingOwnerShipInfo;
  handleInspectionInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  incomingBuilding: BuildingDTO;
  incomingUnit: UnitDTO;
  parentEditMode?: boolean;
}

const BuildingOwnershipInfoComponent = ({
  ownershipInfo,
  handleCloseParent,
  handleInspectionInfoChangeInParent,
  incomingBuilding,
  incomingUnit,
  parentEditMode = true,
}: BuildingOwnershipInfoComponentProps) => {
  const [editMode, setEditMode] = useState(parentEditMode);
  const [editedOwnershipInfo, setEditedOwnershipInfo] = useState(ownershipInfo);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { session } = useSession();
  const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
  const [building, setBuilding] = useState(incomingBuilding);
  const [unit, setUnit] = useState(incomingUnit);


  useEffect(() => {
    if (incomingBuilding?.id !== building?.id) {
      setBuilding(incomingBuilding);
    }
  }, [incomingBuilding]);

  useEffect(() => {
    if (incomingUnit?.id !== unit?.id) {
      setUnit(incomingUnit);
    }
  }, [incomingUnit]);

  useEffect(() => {
    setEditedOwnershipInfo(ownershipInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch

  const handleSave = async () => {
    setHasUnsavedChanges(false);
    try {
      await fetchWithAuth(
        `${API_URL}/building/building-ownership-info`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editedOwnershipInfo),
        },
        session,
      );
      toast.success("Eigenaars informatie succesvol bijgewerkt!");
    } catch (e) {
      console.error(e);
      toast.error(
        "Er is een fout opgetreden bij het bijwerken van de eigendoms informatie.",
      );
    }
  };

  const updateOwnershipInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedOwnershipInfo((prev: any) => ({ ...prev, ...updatedFields }));

    if (handleInspectionInfoChangeInParent) {
      handleInspectionInfoChangeInParent({
        ...ownershipInfo,
        ...updatedFields,
      });
    }
  };

  const handleFileModalClose = () => {
    setFileModalOpen(false);
  };

  const handleFileModalSave = async (fileLogData: any) => {
    console.log(fileLogData);
    const newBuildingDeedsFileLogs = fileLogData.customData?.savedBuildingOwnershipInfo?.buildingDeedsFileLogs;
    if (newBuildingDeedsFileLogs) {
      setEditedOwnershipInfo((prev) => ({
        ...prev,
        buildingDeedsFileLogs: newBuildingDeedsFileLogs,
      }));
    }
    handleFileModalClose();
  };

  const getPreEmptionStatusColor = (status: PreEmptionRightPresent) => {
    switch (status) {
      case PreEmptionRightPresent.OK:
        return "bg-green-500";
      case PreEmptionRightPresent.NOT_OK:
        return "bg-red-500";
      default:
        return "bg-transparent";
    }
  };

  const updatePreEmptionRightPresentDescription = (value: string) => {
    updateOwnershipInfo({ preEmptionRightPresentDescription: value });
  };

  return (
    <section className="flex flex-col gap-0 rounded-md bg-white">
      <SectionHeader
        title="Eigendomsinformatie"
        icon={<Document />}
        editMode={editMode}
        sticky={false}
        onEditClick={() => setEditMode(!editMode)}
        onSaveClick={() => handleSave()}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={handleCloseParent}
        hideCloseAction={true}
      />
      <div className="p-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* PreEmptionRightPresent Selection */}
          <div className="flex flex-col gap-3 rounded-md border border-gray-200 p-4">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full ${getPreEmptionStatusColor(
                  editedOwnershipInfo.preEmptionRightPresent,
                )}`}
              />
              <p>Voorkooprecht aanwezig?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(PreEmptionRightPresent).map((value) => ({
                label: value,
                value,
              }))}
              value={editedOwnershipInfo.preEmptionRightPresent}
              editMode={editMode}
              onChange={(value) =>
                updateOwnershipInfo({ preEmptionRightPresent: value })
              }
            />
          </div>

          {/* Description for PreEmptionRightPresent */}
          {editedOwnershipInfo.preEmptionRightPresent ===
            PreEmptionRightPresent.NOT_OK && (
            <div className="flex flex-col gap-3 rounded-md border border-gray-200 p-4">
              <header className="flex items-center gap-2 text-sm font-medium">
                <p>Beschrijving van het voorkooprecht</p>
              </header>
              <EditableTextareaComponent
                label="Geef opmerkingen of toelichting hier"
                value={
                  editedOwnershipInfo.preEmptionRightPresentDescription || ""
                }
                editMode={editMode}
                onChange={(value) =>
                  updatePreEmptionRightPresentDescription(value)
                }
              />
            </div>
          )}
          {/* Any additional components to fully flesh out the building's ownership details */}
        </div>

        <div className="col-span-full mt-8 flex flex-col gap-3">
          <FileLogTable
            acceptedLabels={[FileLabel.AttestAankoop, FileLabel.Basisakte, FileLabel.Biedbrief, FileLabel.Eigendomsakte, FileLabel.LOI, FileLabel.OnderhandseOvereenkomst, FileLabel.ReglementMedeEigendom, FileLabel.SPA]}
            building={building}
            onUploadClick={() => setFileModalOpen(true)}
          />
        </div>
        {/* Modal for File Upload */}
        <ModalUploadFileLog
          isOpen={fileModalOpen}
          onClose={() => setFileModalOpen(false)}
          onSave={(fileLogData) => handleFileModalSave(fileLogData)}
          building={building}
          unit={unit}
          incomingCustomData={{
            buildingOwnershipInfoId: editedOwnershipInfo?.id,
          }}
          customLabels={[
            FileLabel.AttestAankoop,
            FileLabel.Basisakte,
            FileLabel.Biedbrief,
            FileLabel.Eigendomsakte,
            FileLabel.LOI,
            FileLabel.OnderhandseOvereenkomst,
            FileLabel.ReglementMedeEigendom,
            FileLabel.SPA,
          ]}
        />
      </div>
    </section>
  );
};

export default BuildingOwnershipInfoComponent;
